import { FC, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  AutocompleteRenderInputParams,
  Box,
  Divider,
  ElevatedPopper,
  FormInputText,
  IconButton,
  InputLabel,
  Modal,
  Select,
  Svg,
} from '@hopskipdrive/react-components';
import { Button } from '@rideiq/shared/components/Button';
import { Text } from '@rideiq/shared/components/Text';
import { useCancelReason } from '@rideiq/shared/hooks/useCancelReason';
import pluralize from 'pluralize';

import { useSmsConfirmation } from '../../../../hooks/useSmsConfirmation';
import { colorTokens } from '../../../../shared/components/variables';
import { useToastStore } from '../../../../shared/stores/toastStore';

import { Close } from './styles';
import { cancelRidesValidationSchema } from './validationSchema';

interface IBulkCancelRides {
  selectedRideIds: number[];
  onClose: () => void;
  handleCancelRides: ({ cancellationReasonKey }) => void;
  cancelRidesResponse: string;
}
export const BulkCancelRides: FC<IBulkCancelRides> = ({
  selectedRideIds,
  onClose,
  handleCancelRides,
  cancelRidesResponse,
}) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const divRef = useRef<HTMLDivElement>(null);
  const { addToast } = useToastStore();
  const { smsConfirmationMessage } = useSmsConfirmation();

  const onCloseActions = () => {
    setIsModalOpen(false);
    onClose();
  };

  useEffect(() => {
    if (cancelRidesResponse) {
      addToast({
        severity: 'success',
        message: `${cancelRidesResponse} ${smsConfirmationMessage}`,
      });
      onCloseActions();
    }
  }, [cancelRidesResponse]);

  return (
    <div ref={divRef} data-testid="elevated-popper">
      <ElevatedPopper isOpen={selectedRideIds.length > 0} anchorElRef={divRef.current} placement="bottom">
        <CancelButton selectedRideCount={selectedRideIds.length} setIsModalOpen={() => setIsModalOpen(true)} />
        <Divider flexItem style={{ margin: '4px 14px 0', height: 32, width: 1 }} variant="middle" />
        <Close onClick={onCloseActions} data-testid="close-bulk-cancel">
          <Svg icon="close" />
        </Close>
      </ElevatedPopper>
      {isModalOpen && (
        <CancellationReasonsModal onCancel={() => setIsModalOpen(false)} handleCancelRides={handleCancelRides} />
      )}
    </div>
  );
};

const CancelButton = ({ selectedRideCount, setIsModalOpen }) => (
  <Button style={{ height: 40 }} color="primary" size="large" variant="contained" onClick={() => setIsModalOpen(true)}>
    {`Cancel ${pluralize('Ride', selectedRideCount, true)}`}
  </Button>
);

const CancellationReasonsModal = ({ onCancel, handleCancelRides }) => {
  const { cancelReasons } = useCancelReason('single');
  const {
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      cancellationReasonKey: '',
    },
    resolver: yupResolver(cancelRidesValidationSchema),
  });

  return (
    <Modal open={true} data-testid="cancellation-modal">
      <Modal.Title sx={{ padding: '20px 24px 6px' }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Text variant="h1" fontWeight={700}>
            {'Cancel Selected Rides'}
          </Text>
          <IconButton aria-label="Close bulk cancel rides modal" onClick={onCancel} sx={{ padding: 0 }}>
            <Svg height="20px" icon="close-outlined" />
          </IconButton>
        </Box>
      </Modal.Title>
      <Modal.Content sx={{ padding: '0px 24px 30px' }}>
        <form id="bulk-cancel-rides-form" onSubmit={handleSubmit((formData) => handleCancelRides(formData))}>
          <Text
            variant="subtitle2"
            style={{
              color: colorTokens.text.medium,
              marginBottom: '0.75rem',
              marginTop: 0,
              fontSize: '14px',
              fontWeight: 400,
              letterSpacing: '0.1px',
            }}
          >
            {'All selected rides will be canceled. This action cannot be undone.'}
          </Text>
          <Box display="flex" flexDirection="column" gap="3px">
            <InputLabel htmlFor="cancellationReasonKey" fieldtype="required" style={{ fontWeight: 300 }}>
              Reason
            </InputLabel>
            <Controller
              control={control}
              name="cancellationReasonKey"
              render={({ field: { onChange, value } }) => (
                <Select
                  options={cancelReasons}
                  value={value}
                  setValue={onChange}
                  data-testid="cancel-reason-select"
                  renderInput={(params: AutocompleteRenderInputParams) => (
                    <FormInputText
                      {...params}
                      variant="outlined"
                      placeholder="Select a reason"
                      fullWidth
                      error={!!errors.cancellationReasonKey}
                      helperText={errors.cancellationReasonKey?.message}
                    />
                  )}
                />
              )}
            />
          </Box>
        </form>
      </Modal.Content>
      <Modal.Actions sx={{ padding: '20px 24px 16px', gap: '10px' }}>
        <Button color="tertiary" onClick={onCancel} loading={isSubmitting}>
          Never mind
        </Button>
        <Button form="bulk-cancel-rides-form" color="primary" type="submit" loading={isSubmitting}>
          {isSubmitting ? 'Confirming...' : 'Confirm'}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};
